// Funzioni di supporto di Smart Lotto (senza AI): date rapide, compressione
// delle foto nel telefono e lettura del barcode direttamente dalla foto.

const pad = (n) => String(n).padStart(2, "0");
const lastDay = (y, m) => new Date(y, m, 0).getDate();

function build(y, m, d) {
  if (y < 100) y += 2000;
  if (!(m >= 1 && m <= 12) || y < 2000 || y > 2099) return null;
  if (d === "end") d = lastDay(y, m);
  if (!(d >= 1 && d <= lastDay(y, m))) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

// Scrittura rapida della data senza calendario:
//  151026 / 15102026 / 15/10/26 -> 15/10/2026
//  TMC: 1026 / 10/2026 -> fine mese (31/10/2026)
//  Scadenza a 4 cifre: 1510 -> 15/10 dell'anno in corso (o del prossimo se già passato)
// Restituisce la data ISO, o null se non interpretabile (mai una data "indovinata").
export function parseQuickDate(input, kind = "scadenza", today = new Date()) {
  const text = String(input || "").trim();
  if (!text) return null;
  if (/[^\d\s./-]/.test(text)) return null;
  const parts = text.split(/[\s./-]+/).filter(Boolean);
  if (parts.length === 3) return build(+parts[2], +parts[1], +parts[0]);
  if (parts.length === 2) {
    const [a, b] = parts.map(Number);
    if (parts[1].length >= 2 && kind === "tmc") return build(b, a, "end") || build(today.getFullYear(), b, a);
    return build(today.getFullYear(), b, a) || build(b, a, "end");
  }
  const d = text.replace(/\D/g, "");
  if (d.length === 8) return build(+d.slice(4), +d.slice(2, 4), +d.slice(0, 2)) || build(+d.slice(2), +d.slice(0, 2), "end");
  if (d.length === 6) return build(+d.slice(4), +d.slice(2, 4), +d.slice(0, 2)) || build(+d.slice(2), +d.slice(0, 2), "end");
  if (d.length === 4) {
    const asMonthYear = () => build(+d.slice(2), +d.slice(0, 2), "end");
    const asDayMonth = () => {
      const year = today.getFullYear();
      const iso = build(year, +d.slice(2), +d.slice(0, 2));
      if (!iso) return null;
      const limit = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
      return new Date(iso) < limit ? build(year + 1, +d.slice(2), +d.slice(0, 2)) : iso;
    };
    return kind === "tmc" ? (asMonthYear() || asDayMonth()) : (asDayMonth() || asMonthYear());
  }
  return null;
}

export const formatIt = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");

// Riduce la foto a 2000 px (lato lungo) in JPEG prima dell'invio: upload rapido
// anche in cantina/cella con poco segnale, testo ancora leggibile.
export function compressImage(file, maxSide = 2000, quality = 0.85) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => { URL.revokeObjectURL(url); resolve(blob || file); }, "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

let readerPromise = null;
async function getReader() {
  if (!readerPromise) {
    readerPromise = Promise.all([import("@zxing/browser"), import("@zxing/library")]).then(([browser, lib]) => {
      const hints = new Map();
      hints.set(lib.DecodeHintType.TRY_HARDER, true);
      hints.set(lib.DecodeHintType.ASSUME_GS1, true);
      hints.set(lib.DecodeHintType.POSSIBLE_FORMATS, [
        lib.BarcodeFormat.EAN_13, lib.BarcodeFormat.EAN_8, lib.BarcodeFormat.UPC_A, lib.BarcodeFormat.UPC_E,
        lib.BarcodeFormat.CODE_128, lib.BarcodeFormat.DATA_MATRIX, lib.BarcodeFormat.QR_CODE,
      ]);
      return new browser.BrowserMultiFormatReader(hints);
    });
  }
  return readerPromise;
}

// Cerca un barcode/DataMatrix/QR nella foto già scattata: nessuna fotocamera "dal vivo" necessaria.
export async function decodeBarcodeFromBlob(blob) {
  const url = URL.createObjectURL(blob);
  try {
    const reader = await getReader();
    const result = await reader.decodeFromImageUrl(url);
    return result?.getText?.() || null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function startLiveScan(videoEl, onText) {
  const reader = await getReader();
  return reader.decodeFromConstraints({ video: { facingMode: "environment" } }, videoEl, (result) => {
    if (result) onText(result.getText());
  });
}

export const vibrate = (ms = 60) => { try { navigator.vibrate?.(ms); } catch { /* non supportato */ } };

// "thumb" = miniatura leggera (~30 KB) per gli elenchi; la foto intera solo quando si ingrandisce.
export const photoUrl = (id, size) => `/api/app/lot-photos/${id}${size === "thumb" ? "?size=thumb" : ""}`;
export const thumbUrl = (url) => (url && url.startsWith("/api/app/lot-photos/") && !url.includes("?") ? `${url}?size=thumb` : url);
